import { useDateFns, useProvider, useHasAccess, AccessList } from '~/Hooks';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import { ExtendedEvent } from '../ExtendedEvent.model';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { EventType } from '~/Models/DTO/Event.model';
import { isNil } from 'lodash-es';
import { useEventFieldValidator } from './useEventFieldValidator';
import { MAX_CANCELATION_WINDOW_INPUT_USC } from '../../constants';
import { EventGroupDefaultValue } from '~/Models/DTO/EventGroup.model';

const isDetailsSectionDone = (color?: string, categoryId?: number) => !!color && !isNil(categoryId);

export const useOnChangeEventGroupForm = (
  setIsGeneralSectionDone: React.Dispatch<React.SetStateAction<boolean>>,
  setIsDetailsSectionDone: React.Dispatch<React.SetStateAction<boolean>>,
  setIsScheduleSectionDone: React.Dispatch<React.SetStateAction<boolean>>,
  setIsSettingsSectionDone: React.Dispatch<React.SetStateAction<boolean>>,
  setEventGroup: React.Dispatch<React.SetStateAction<ExtendedEventGroup>>,
  eventType: EventType,
) => {
  const { formatDate, parse } = useDateFns();
  const { isUsingThePartnerTool } = useProvider();
  const { timeFieldValidator } = useEventFieldValidator();
  const { hasAccess } = useHasAccess();

  return (data: Partial<ExtendedEventGroup>) => {
    setEventGroup((prevEventGroup) => {
      const mergedEventGroup = {
        ...prevEventGroup,
        ...data,
        seats: data.seats || prevEventGroup.seats || 0,
      };
      const refundValidation = !mergedEventGroup.publicCancellationWindowOpen
        ? EventGroupDefaultValue.publicCancellationWindowEnd
        : timeFieldValidator({
            number: mergedEventGroup.publicCancellationRefundEndAsNumber,
            type: mergedEventGroup.publicCancellationRefundEndType,
          });
      const cleanedData = {
        publicCancellationRefundEnd: refundValidation,
        publicCancellationWindowEnd: isUsingThePartnerTool
          ? refundValidation
          : formatDate(parse(`${0}`, 'HH', new Date()).toUTCString(), '-HH:mm:ss'),
        publicBookingWindowStart: timeFieldValidator({
          number: mergedEventGroup.publicBookingWindowStartAsNumber,
          type: mergedEventGroup.publicBookingWindowStartType,
          bookingWindowOpen: mergedEventGroup.bookingWindowOpen,
        }),
        publicBookingWindowEnd: timeFieldValidator({
          number: mergedEventGroup.publicBookingWindowEndAsNumber,
          type: mergedEventGroup.publicBookingWindowEndType,
          bookingWindowOpen: mergedEventGroup.bookingWindowOpen,
        }),
      };
      const bookingGroupIds: string[] | undefined = mergedEventGroup?.bookingGroupIds;

      const generalSectionDone =
        eventType === EventType.FREE_TRAINING
          ? !!mergedEventGroup.name && !!mergedEventGroup.locationId
          : !!mergedEventGroup.name &&
            !!mergedEventGroup.trainerIds?.length &&
            !!mergedEventGroup.locationId &&
            mergedEventGroup.seats > 0;

      const validationForPartnerTool =
        hasAccess(AccessList.CANCELATION_WINDOW_PARTNER_TOOL) &&
        Number(mergedEventGroup.publicCancellationRefundEndAsNumber) >
          MAX_CANCELATION_WINDOW_INPUT_USC;

      const detailsSectionDone = isDetailsSectionDone(
        mergedEventGroup.color,
        mergedEventGroup.categoryId,
      );
      const scheduleSectionDone = !!mergedEventGroup?.timeSeries.some((t) => !t.shouldDelete);
      const settingsSectionDone =
        !mergedEventGroup.publicCancellationWindowOpen ||
        (!!mergedEventGroup.publicCancellationRefundEndAsNumber && !validationForPartnerTool) ||
        (mergedEventGroup.bookingWindowOpen &&
          !!mergedEventGroup.publicBookingWindowStartAsNumber <
            !!mergedEventGroup.publicBookingWindowEndAsNumber);

      setIsGeneralSectionDone(generalSectionDone);
      setIsDetailsSectionDone(detailsSectionDone);
      setIsScheduleSectionDone(scheduleSectionDone);
      setIsSettingsSectionDone(settingsSectionDone);

      return {
        ...mergedEventGroup,
        ...cleanedData,
        seats: Math.max(mergedEventGroup?.seats || 0, 0),
        bookingGroupIds,
      };
    });
  };
};

export const markEventSectionsDone = (
  event: Partial<ExtendedEvent>,
  bookingSummary: IBookingSummaryEvent,
  setIsGeneralSectionDone: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const generalSectionDone =
    event.type === EventType.FREE_TRAINING
      ? !!event.name && !!event.locationId && !!event.startTime
      : !!event.name &&
        !!event.locationId &&
        !!event.seats &&
        (!bookingSummary?.bookingsBooked || event.seats >= bookingSummary.bookingsBooked) &&
        !!event.startTime &&
        !!event.endTime &&
        event.startTime < event.endTime;

  setIsGeneralSectionDone(generalSectionDone);
};
